import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@calm-web/design-system';

import { DiscountLandingProps } from '@/components/discountLanding/DiscountLandingWrapper/types';
import { useFormattedCurrency } from '@/hooks/purchase';
import { usePricesState } from '@/hooks/store';

import { featureItems } from './data';
import messages from './messages';
import {
	ButtonWrapper,
	CircleCheck,
	DiscountPrice,
	FeatureBackground,
	FeatureCell,
	FeatureHeading,
	FeatureListEnd,
	FeatureRow,
	FeatureText,
	Heading,
	HiddenLabel,
	LimitedTimeOfferText,
	PriceWrapper,
	Pricing,
	StrikethruPrice,
	Subheading,
	Table,
	TableWrapper,
	Thead,
	Wrapper,
} from './styles';

const Unlock = ({ onClick, isDiscounted }: DiscountLandingProps) => {
	const { formatMessage } = useIntl();
	const prices = usePricesState();
	const formatCurrency = useFormattedCurrency();

	const [bgTop, setBgTop] = useState(0);
	const tableWrapperEl = useRef<HTMLDivElement>(null);

	const initBgTop = () => {
		const tableWrapperElTop = tableWrapperEl.current?.getBoundingClientRect().top;
		const tableElTop = tableWrapperEl.current?.querySelector('table')?.getBoundingClientRect().top;
		if (tableElTop && tableWrapperElTop) {
			setBgTop(tableElTop - tableWrapperElTop);
		}
	};

	useEffect(() => {
		if (tableWrapperEl) {
			initBgTop();
		}
		window.addEventListener('resize', initBgTop);

		return () => {
			window.removeEventListener('resize', initBgTop);
		};
	}, [tableWrapperEl]);

	return (
		<Wrapper>
			<TableWrapper ref={tableWrapperEl}>
				<FeatureBackground $top={bgTop} />
				<Heading id="premium-benefits">{formatMessage(messages.heading)}</Heading>
				<Subheading>{formatMessage(messages.subheading)}</Subheading>
				<Table aria-labelledby="premium-benefits">
					<Thead>
						<tr>
							<FeatureCell as="td" />
							<FeatureCell as="th">
								<FeatureHeading>{formatMessage(messages.basicHeading)}</FeatureHeading>
							</FeatureCell>
							<FeatureCell as="th">
								<FeatureHeading>{formatMessage(messages.premiumHeading)}</FeatureHeading>
							</FeatureCell>
						</tr>
					</Thead>
					<tbody>
						{featureItems.map(feature => {
							return (
								<FeatureRow key={formatMessage(feature.description)}>
									<FeatureText>{formatMessage(feature.description)}</FeatureText>
									{feature.isLimitedLabel && <FeatureText>{formatMessage(messages.limitedText)}</FeatureText>}
									{feature.isLimitedCheck && (
										<FeatureCell aria-label={formatMessage(messages.unlocked)}>
											<CircleCheck />
										</FeatureCell>
									)}
									{!feature.isLimitedLabel && !feature.isLimitedCheck && (
										<FeatureCell aria-label={formatMessage(messages.locked)} />
									)}
									{feature.isUnlimitedLabel ? (
										<FeatureText>{formatMessage(messages.unlimitedText)}</FeatureText>
									) : (
										<FeatureCell aria-label={formatMessage(messages.unlocked)}>
											<CircleCheck />
										</FeatureCell>
									)}
								</FeatureRow>
							);
						})}
					</tbody>
					<FeatureListEnd>
						<tr>
							<FeatureCell />
							<LimitedTimeOfferText>
								{isDiscounted && formatMessage(messages.limitedTimeOfferText)}
							</LimitedTimeOfferText>
							<Pricing>
								{isDiscounted && (
									<PriceWrapper>
										<HiddenLabel>{formatMessage(messages.originalPriceLabel)}</HiddenLabel>
										<StrikethruPrice>{formatCurrency(prices?.original?.yearly)}</StrikethruPrice>
									</PriceWrapper>
								)}
								<PriceWrapper>
									<HiddenLabel>{formatMessage(messages.discountPriceLabel)}</HiddenLabel>
									<DiscountPrice>{formatCurrency(prices.current.yearly)}</DiscountPrice>
								</PriceWrapper>
							</Pricing>
						</tr>
					</FeatureListEnd>
				</Table>
			</TableWrapper>
			<ButtonWrapper>
				<Button
					fullWidth
					maxWidth="360px"
					backgroundColor="gradientCalmBrand"
					textColor="white"
					onPress={() =>
						onClick({
							section: 'unlock',
							copy: messages.cta.defaultMessage,
						})
					}
				>
					{formatMessage(messages.cta)}
				</Button>
			</ButtonWrapper>
		</Wrapper>
	);
};

export default Unlock;
